import { TandemTool } from "../../base/TandemTool.js";
import { logger } from "../../../../../../core/logger.js";

/**
 * Exact string search-and-replace in a file (Tandem Tool layer).
 * Uses tandemAPI.editFile (`tandem:edit-file`) — separate from workspace search-replace IPC.
 */
export class EditFileTool extends TandemTool {
  constructor() {
    super();
    this.toolName = "editFileTool";
  }

  /**
   * @param {string} path
   * @param {string} oldString
   * @param {string} newString
   * @param {boolean} [replaceAll=false]
   * @returns {Promise<Object>}
   */
  async editFile(path, oldString, newString, replaceAll = false) {
    try {
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }
      if (
        oldString === undefined ||
        oldString === null ||
        typeof oldString !== "string" ||
        oldString.length === 0
      ) {
        return this.returnError(
          {},
          "old_string is required and must be a non-empty string"
        );
      }
      if (
        newString === undefined ||
        newString === null ||
        typeof newString !== "string"
      ) {
        return this.returnError(
          {},
          "new_string is required and must be a string"
        );
      }

      const api = this.getTandemLocalAppApi();
      if (!api?.editFile) {
        return this.returnError(
          {},
          "Tandem edit file requires Revolt Desktop (tandemAPI.editFile)."
        );
      }

      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return this.returnError(
          {},
          "Tandem has no project folder set (working directory path)."
        );
      }

      const normalizedPath = this.normalizeAndValidatePath(path);
      if (!normalizedPath) {
        return this.returnError({}, "Invalid file path");
      }
      const relativePath = normalizedPath.replace(/^\/+/, "");
      if (!relativePath) {
        return this.returnError({}, "Invalid file path");
      }

      const res = await api.editFile({
        rootPath: root,
        relativePath,
        oldString,
        newString,
        replaceAll,
      });

      if (!res?.success) {
        return this.returnError(
          {},
          res?.error || "Failed to apply edit file on disk"
        );
      }

      logger.log(
        { tags: "tools|tandem|editfile|info", color1: "lime" },
        `Tandem edit file: ${normalizedPath}`,
        {
          replacementCount: res.replacementCount,
          replaceAll,
        }
      );

      return this.returnSuccess(
        {
          path: normalizedPath,
          replacementCount: res.replacementCount,
          replaceAll,
          size: res.size ?? null,
        },
        `Successfully replaced ${res.replacementCount} occurrence(s) in file`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|tandem|editfile|error", color1: "red" },
        `Tandem edit file failed: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
